/**
 * La sesión de edición, guardada para sobrevivir a un reinicio del servidor.
 *
 * Guardar reescribe `rooms.ts`, `tsx watch` reinicia el servidor y la conexión cae.
 * El efecto de sesión de `App.tsx` reacciona destruyendo el `RoomStage` y creando
 * otro — que es lo correcto, porque el nuevo tiene que leer el catálogo recién
 * escrito — pero con él se iba el `EditorOverlay`: el editor se cerraba solo en cada
 * guardado y había que volver a pulsar F2 y reseleccionar la puerta.
 *
 * Peor todavía con un reinicio ajeno al editor (tocar código de servidor): se perdía
 * el borrador sin guardar entero, sin aviso.
 *
 * Vive en `sessionStorage` y no en memoria porque Vite también puede recargar la
 * página, y en `sessionStorage` y no en `localStorage` porque es estado de una sesión
 * de trabajo, no una preferencia: cerrar la pestaña debe olvidarlo.
 */

const KEY = "clubap:editor";

export interface EditorSession {
  roomId: string;
  open: boolean;
  elementId: string | null;
  /**
   * El borrador serializado, SÓLO si tenía cambios sin guardar.
   *
   * Restaurar un borrador ya guardado sería redundante —el catálogo nuevo ya lo
   * contiene— y encima resucitaría geometría vieja si el archivo cambió por otra vía.
   */
  draft: string | null;
}

export function saveEditorSession(session: EditorSession): void {
  if (!import.meta.env.DEV) return;

  try {
    sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // Cuota llena o almacenamiento bloqueado: perder la restauración es molesto,
    // tumbar el editor por ello sería peor.
  }
}

export function loadEditorSession(): EditorSession | null {
  if (!import.meta.env.DEV) return null;

  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<EditorSession> | null;
    if (!parsed || typeof parsed.roomId !== "string") return null;

    return {
      roomId: parsed.roomId,
      open: parsed.open === true,
      elementId: typeof parsed.elementId === "string" ? parsed.elementId : null,
      draft: typeof parsed.draft === "string" ? parsed.draft : null,
    };
  } catch {
    return null;
  }
}

export function clearEditorSession(): void {
  if (!import.meta.env.DEV) return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ver arriba */
  }
}

/*
 * Dónde dejó el usuario cada panel flotante del editor.
 *
 * En `localStorage` y no en `sessionStorage`, al revés que la sesión de arriba, y por
 * la misma razón que allí se decidió lo contrario: esto SÍ es una preferencia. Dónde
 * te gusta tener el formulario de puertas no es estado de una sesión de trabajo, y
 * recolocar tres paneles en cada pestaña nueva sería exactamente el tipo de fricción
 * que hace que la gente deje de moverlos.
 */
const LAYOUT_KEY = "clubap:editor:layout";

export interface PanelPosition {
  x: number;
  y: number;
}

export type PanelLayout = Record<string, PanelPosition>;

export function loadPanelLayout(): PanelLayout {
  if (!import.meta.env.DEV) return {};

  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    const layout: PanelLayout = {};

    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      const point = value as Partial<PanelPosition> | null;
      // Una coordenada corrupta dejaría un panel en NaN, que se pinta fuera de la
      // pantalla y parece que el editor no abrió.
      if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
        layout[id] = { x: point.x as number, y: point.y as number };
      }
    }

    return layout;
  } catch {
    return {};
  }
}

export function savePanelPosition(id: string, position: PanelPosition): void {
  if (!import.meta.env.DEV) return;

  try {
    const layout = loadPanelLayout();
    layout[id] = position;
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    /* ver arriba */
  }
}

export function clearPanelLayout(): void {
  if (!import.meta.env.DEV) return;
  try {
    localStorage.removeItem(LAYOUT_KEY);
  } catch {
    /* ver arriba */
  }
}
